import {AiChatCostType, ensureAiChatCreditsForCost} from '@Libraries/ai-chat.library';

interface MakeTextEditableInitialResponse {
  jobId: string;
  posterHashedId?: string;
  layersReady?: boolean;
  debugViewUrl?: string | null;
}

interface MakeTextEditableResult {
  posterHashedId: string;
  posterUrl: string;
}

export interface MakeTextEditableOutcome extends MakeTextEditableResult {
  jobId: string;
  debugViewUrl: string | null;
}

/**
 * Pre-created-poster "Editable layers" start: pre-creates the poster the editor opens immediately and
 * enqueues the layering job. Unlike {@see createPosterFromAiChatAsset} it does NOT poll — the editor tab
 * polls {@see pollMakeTextEditableLayersStatus} and reloads the poster in place once layers are ready.
 */
export interface MakeTextEditablePreCreatedStart {
  jobId: string;
  posterHashedId: string;
  layersReady: boolean;
  debugViewUrl: string | null;
}

export enum MakeTextEditableErrorCode {
  INSUFFICIENT_CREDITS = 'insufficient_credits',
  ERROR = 'error',
}

export type MakeTextEditableError = Error & {code: MakeTextEditableErrorCode};

function throwMakeTextEditableError(message: string, code: MakeTextEditableErrorCode): never {
  const err: MakeTextEditableError = Object.assign(new Error(message), {code});
  throw err;
}

const isMakeTextEditableErrorCode = (value: string): value is MakeTextEditableErrorCode => {
  const knownCodes: string[] = Object.values(MakeTextEditableErrorCode);
  return knownCodes.includes(value);
};

const toMakeTextEditableErrorCode = (value: string): MakeTextEditableErrorCode => {
  return isMakeTextEditableErrorCode(value) ? value : MakeTextEditableErrorCode.ERROR;
};

const checkMakeTextEditableStatus = async (initialResponse: {jobId: string}): Promise<MakeTextEditableResult | null> => {
  return (await window.PMW.writeLocal('imagetodesign/createFromAiChatAssetStatus', {
    jobId: initialResponse.jobId,
  })) as MakeTextEditableResult | null;
};

export enum MakeTextEditableTextRemovalMethod {
  NANO_BANANA = 'nanoBanana',
  GPT_IMAGE_2 = 'gptImage2',
}

export const EDITABLE_LAYERS_TEXT_REMOVAL_METHOD = MakeTextEditableTextRemovalMethod.NANO_BANANA;

const MAKE_TEXT_EDITABLE_POLL_TIMEOUT_MS = 600000;

export const ensureMakeTextEditableCredits = async (): Promise<boolean> => {
  return ensureAiChatCreditsForCost(AiChatCostType.MAKE_TEXT_EDITABLE_COST);
};

/**
 * Long-polled create flow. Kicks off /imagetodesign/createFromAiChatAssetStart
 * (which enqueues the I2D job on SQS and returns its jobId), then polls
 * /imagetodesign/createFromAiChatAssetStatus via window.PMW.pollingAjaxCallAsync
 * until the consumer writes the final result. Mirrors the animation generation
 * pattern in `ai-chat-animation-generation.library.ts`.
 */
export const createPosterFromAiChatAsset = async (assetHashedFilename: string, textRemovalMethod?: MakeTextEditableTextRemovalMethod): Promise<MakeTextEditableOutcome> => {
  let capturedJobId: string | undefined;
  let capturedDebugViewUrl: string | null = null;

  const initiate = async (): Promise<MakeTextEditableInitialResponse> => {
    const response = (await window.PMW.writeLocal('imagetodesign/createFromAiChatAssetStart', {
      assetHashedFilename,
      ...(textRemovalMethod ? {textRemovalMethod} : {}),
    })) as MakeTextEditableInitialResponse;
    capturedJobId = response.jobId;
    capturedDebugViewUrl = response.debugViewUrl ?? null;
    return response;
  };

  const result = (await window.PMW.pollingAjaxCallAsync(initiate, checkMakeTextEditableStatus, MAKE_TEXT_EDITABLE_POLL_TIMEOUT_MS)) as MakeTextEditableResult | undefined | string;
  const validated = validateMakeTextEditableResult(result);

  if (capturedJobId === undefined) {
    throwMakeTextEditableError('Make text editable resolved without capturing a jobId', MakeTextEditableErrorCode.ERROR);
  }

  return {...validated, jobId: capturedJobId, debugViewUrl: capturedDebugViewUrl};
};

/**
 * Normalises a poll result into a {@see MakeTextEditableResult}, throwing a coded
 * {@see MakeTextEditableError} on the three failure shapes the poller can yield: a string error code,
 * a timeout (undefined), or a malformed payload missing the poster fields.
 */
const validateMakeTextEditableResult = (result: MakeTextEditableResult | undefined | string): MakeTextEditableResult => {
  if (typeof result === 'string') {
    throwMakeTextEditableError('Make text editable failed', toMakeTextEditableErrorCode(result));
  }

  if (!result) {
    throwMakeTextEditableError('Make text editable timed out', MakeTextEditableErrorCode.ERROR);
  }

  if (!result.posterHashedId || !result.posterUrl) {
    throwMakeTextEditableError('Make text editable returned an unexpected response', MakeTextEditableErrorCode.ERROR);
  }

  return result;
};

/**
 * Pre-created-poster start for "Editable layers": POSTs createFromAiChatAssetStart with preCreatePoster so
 * the backend pre-creates the poster the editor opens immediately, deducts credits, and enqueues the
 * layering job. Same endpoint as {@see createPosterFromAiChatAsset} (there is no mobile-specific endpoint);
 * this variant just pre-creates + returns the ids — no polling here, the editor tab polls and reloads.
 */
export const startMakeTextEditableWithPreCreatedPoster = async (assetHashedFilename: string, textRemovalMethod?: MakeTextEditableTextRemovalMethod): Promise<MakeTextEditablePreCreatedStart> => {
  const response = (await window.PMW.writeLocal('imagetodesign/createFromAiChatAssetStart', {
    assetHashedFilename,
    preCreatePoster: true,
    ...(textRemovalMethod ? {textRemovalMethod} : {}),
  })) as MakeTextEditableInitialResponse;

  if (!response.posterHashedId) {
    throwMakeTextEditableError('Make text editable did not return a poster', MakeTextEditableErrorCode.ERROR);
  }

  return {
    jobId: response.jobId,
    posterHashedId: response.posterHashedId,
    layersReady: response.layersReady === true,
    debugViewUrl: response.debugViewUrl ?? null,
  };
};

/**
 * Editor-tab poll for the desktop "Editable layers" flow. The job was already started by
 * {@see startMakeTextEditableWithPreCreatedPoster}; this only polls createFromAiChatAssetStatus by job id until the
 * layered poster is ready (or the job errors / times out).
 */
export const pollMakeTextEditableLayersStatus = async (jobId: string): Promise<MakeTextEditableResult> => {
  const initiate = (): Promise<{jobId: string}> => {
    return Promise.resolve({jobId});
  };

  const result = (await window.PMW.pollingAjaxCallAsync(initiate, checkMakeTextEditableStatus, MAKE_TEXT_EDITABLE_POLL_TIMEOUT_MS)) as MakeTextEditableResult | undefined | string;
  return validateMakeTextEditableResult(result);
};
